import { useState, useEffect } from 'react';
import PoliticianCard from './PoliticianCard';
import FilterBar from './FilterBar';
import { api } from './api';

function PoliticiansPage() {
    const [allPoliticians, setAllPoliticians] = useState([]);
    const [filteredPoliticians, setFilteredPoliticians] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAllPoliticians();
    }, []);

    useEffect(() => {
        applyBranchFilter();
    }, [selectedBranch, allPoliticians]);

    const fetchAllPoliticians = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await api.getAllPoliticians();

            // Add branch info to each politician
            const enrichedData = data.map(politician => {
                let branch = 'executive';
                if (politician.district) branch = 'house';
                else if (politician.state && !politician.position) branch = 'senate';

                return { ...politician, branch };
            });

            setAllPoliticians(enrichedData);
            setFilteredPoliticians(enrichedData);
        } catch (err) {
            setError('Failed to load politicians');
            console.error('Error fetching politicians:', err);
        } finally {
            setLoading(false);
        }
    };

    const applyBranchFilter = () => {
        if (selectedBranch === 'all') {
            setFilteredPoliticians(allPoliticians);
        } else {
            const filtered = allPoliticians.filter(p => p.branch === selectedBranch);
            setFilteredPoliticians(filtered);
        }
    };

    const getBranchTitle = (branch) => {
        switch (branch) {
            case 'house': return 'House of Representatives';
            case 'senate': return 'United States Senate';
            case 'executive': return 'Executive Branch';
            default: return 'All Federal Politicians';
        }
    };

    const getBranchDescription = (branch) => {
        switch (branch) {
            case 'house': return '435 members, 2-year terms';
            case 'senate': return '100 members, 6-year terms';
            case 'executive': return 'President, Vice President, and Cabinet';
            default: return 'Monitor the health of all federal politicians';
        }
    };

    const getHealthStats = (politicians) => {
        const total = politicians.length;
        const critical = politicians.filter(p => p.healthScore < 40).length;
        const concerning = politicians.filter(p => p.healthScore >= 40 && p.healthScore < 60).length;
        const avgScore = total > 0 ? Math.round(politicians.reduce((sum, p) => sum + p.healthScore, 0) / total) : 0;

        return { total, critical, concerning, avgScore };
    };

    if (loading) {
        return (
            <div className="page">
                <div className="loading">Loading politicians...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page">
                <div className="error-state">
                    <h2>🚫 Error Loading Data</h2>
                    <p>{error}</p>
                    <button onClick={fetchAllPoliticians} className="branch-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const stats = getHealthStats(filteredPoliticians);

    return (
        <div className="page">
            {/* Branch Filter Tabs */}
            <div className="branch-tabs">
                <button
                    className={`branch-tab ${selectedBranch === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedBranch('all')}
                >
                    🏛️ All Politicians
                </button>
                <button
                    className={`branch-tab ${selectedBranch === 'house' ? 'active' : ''}`}
                    onClick={() => setSelectedBranch('house')}
                >
                    🏛️ House
                </button>
                <button
                    className={`branch-tab ${selectedBranch === 'senate' ? 'active' : ''}`}
                    onClick={() => setSelectedBranch('senate')}
                >
                    🏛️ Senate
                </button>
                <button
                    className={`branch-tab ${selectedBranch === 'executive' ? 'active' : ''}`}
                    onClick={() => setSelectedBranch('executive')}
                >
                    🏛️ Executive
                </button>
            </div>

            {/* Page Header */}
            <div className="page-header">
                <h2>{getBranchTitle(selectedBranch)}</h2>
                <p>{getBranchDescription(selectedBranch)}</p>

                <div className="stats-row">
                    <div className="stat-box">
                        <span className="stat-number">{stats.total}</span>
                        <span className="stat-label">Total Politicians</span>
                    </div>
                    <div className="stat-box critical">
                        <span className="stat-number">{stats.critical}</span>
                        <span className="stat-label">Critical Health</span>
                    </div>
                    <div className="stat-box concerning">
                        <span className="stat-number">{stats.concerning}</span>
                        <span className="stat-label">Concerning Health</span>
                    </div>
                    <div className="stat-box">
                        <span className="stat-number">{stats.avgScore}</span>
                        <span className="stat-label">Avg Health Score</span>
                    </div>
                </div>
            </div>

            <FilterBar
                politicians={filteredPoliticians}
                onFilteredResults={setFilteredPoliticians}
                showStateFilter={selectedBranch !== 'executive'}
            />

            {filteredPoliticians.length === 0 ? (
                <div className="no-results">
                    <h3>No politicians match your current filters</h3>
                    <p>Try adjusting your filter criteria or selecting a different branch.</p>
                </div>
            ) : (
                <div className="politicians-grid">
                    {filteredPoliticians.map(politician => (
                        <PoliticianCard key={politician.id} politician={politician} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default PoliticiansPage;